import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { useCallback, useState, useEffect } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import ValidatorAutocomplete from "features/forms/ValidatorAutocomplete";
import { useLanderTemplates } from "features/lightningLanders/hooks/useLanderTemplates";

interface Template {
  id: number;
  name: string;
}

interface BatchCreateLanderDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (templateId: number, copyOnly: boolean) => Promise<void>;
  selectedAdsCount: number;
}

function BatchCreateLanderDialog({
  open,
  onClose,
  onConfirm,
  selectedAdsCount,
}: BatchCreateLanderDialogProps) {
  const [template, setTemplate] = useState<Template | null>(null);
  const [copyOnly, setCopyOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { templates } = useLanderTemplates();

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTemplate(null);
      setCopyOnly(false);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!template) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(template.id, copyOnly);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [template, copyOnly, onConfirm, onClose]);

  const isFormValid = template !== null;

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ValidatorForm onSubmit={handleSubmit}>
        <DialogTitle>Create Landing Pages for {selectedAdsCount} ad{selectedAdsCount > 1 ? "s" : ""}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Template (Required)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Select a template to use as a base for all landing pages. The AI
                will use this template as a structural and styling base.
              </Typography>
              <ValidatorAutocomplete
                label="Template"
                name="template"
                value={template}
                options={templates.map((t) => ({
                  id: t.id!,
                  name: t.name || "",
                }))}
                getOptionLabel={(option: Template) => option.name}
                onChange={(e) => setTemplate(e.target.value)}
                disabled={isSubmitting}
                validators={["required"]}
                errorMessages={["Template is required"]}
              />
            </Box>
            <Stack >
              <FormControlLabel
                control={
                  <Switch
                    checked={copyOnly}
                    onChange={(e) => setCopyOnly(e.target.checked)}
                    disabled={isSubmitting}
                  />
                }
                label="Copy Only"
              />
              <Typography variant="body2" color="text.secondary" >
                When enabled, only the text content will be updated to align with the ad copy.
                The template structure and styling will remain unchanged.
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || !isFormValid}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </ValidatorForm>
    </CustomDialog>
  );
}

export default BatchCreateLanderDialog;

